import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarRange, CheckCircle2, Plus, Save, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { borrarPeriodoHc, getPeriodosHc, setPeriodoHc } from "@/lib/api-mercado";

const fmtFecha = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const fechaLarga = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })
    : "";

/**
 * Reportes → Periodo de horas críticas (portado del ETRM; sin perfiles en el Portal).
 *
 * CENACE define cada año la ventana dentro de la cual se toman las 100 horas
 * críticas para el Mercado de Balance de Potencia, UNA POR SISTEMA (SIN, BCA y
 * BCS no coinciden: en 2025 SIN fue abr–jun, BCA ago–sep y BCS may–nov).
 * Aquí se captura una ventana por año; el reporte "Dispersión de Horas
 * Críticas" la lee de etrm.configuracion (clave periodo_hc) y solo la muestra
 * como informativo.
 */
const SISTEMAS_HC = ["SIN", "BCA", "BCS"] as const;
type Edit = { anio: number; sistema: string; desde: string; hasta: string };

export function PeriodoHcPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "periodo-hc"], queryFn: getPeriodosHc });

  const [edit, setEdit] = useState<Edit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["admin", "periodo-hc"] });
    qc.invalidateQueries({ queryKey: ["periodo-hc"] });
    qc.invalidateQueries({ queryKey: ["dispersion-hc"] });
  };
  const guardar = useMutation({
    mutationFn: (p: Edit) => setPeriodoHc(p),
    onSuccess: () => {
      setEdit(null);
      setError(null);
      setOk(true);
      invalidar();
      setTimeout(() => setOk(false), 3000);
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo guardar"),
  });
  const borrar = useMutation({
    mutationFn: (p: { anio: number; sistema: string }) => borrarPeriodoHc(p.anio, p.sistema),
    onSuccess: () => {
      setError(null);
      invalidar();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo quitar"),
  });

  const editValido =
    !!edit &&
    edit.anio >= 2017 &&
    edit.anio <= 2100 &&
    !!edit.desde &&
    !!edit.hasta &&
    edit.desde <= edit.hasta &&
    edit.desde.startsWith(String(edit.anio)) &&
    edit.hasta.startsWith(String(edit.anio));

  // El backend devuelve un renglón por año con sus ventanas; aquí se aplana a
  // una fila por (año, sistema), que es como se captura y se borra.
  const filas = useMemo(
    () =>
      (q.data?.periodos ?? []).flatMap((p) =>
        SISTEMAS_HC.filter((s) => p.sistemas[s]).map((s) => ({
          anio: p.anio, sistema: s as string, fuente: p.fuente, ...p.sistemas[s],
        })),
      ),
    [q.data],
  );

  const nuevoAnio = () => {
    const anios = filas.map((p) => p.anio);
    const a = anios.length ? Math.max(...anios) + 1 : new Date().getFullYear();
    // Propone el mismo lapso del último SIN capturado, ajustado al año nuevo.
    const ult = filas.find((p) => p.anio === Math.max(...anios) && p.sistema === "SIN");
    const md = (iso: string) => iso.slice(4); // "-MM-DD"
    setEdit({
      anio: a,
      sistema: "SIN",
      desde: ult ? `${a}${md(ult.desde)}` : `${a}-02-21`,
      hasta: ult ? `${a}${md(ult.hasta)}` : `${a}-10-09`,
    });
  };
  const editar = (p: { anio: number; sistema: string; desde: string; hasta: string }) =>
    setEdit({ anio: p.anio, sistema: p.sistema, desde: p.desde, hasta: p.hasta });

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <CalendarRange className="w-5 h-5 text-slate-500" /> Periodo de horas críticas (CENACE)
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Ventana del año dentro de la cual CENACE toma las <b>100 horas críticas</b> para el
          Mercado de Balance de Potencia. Hay <b>una por año y por sistema</b> — SIN, BCA y BCS
          tienen ventanas distintas. El
          reporte <b>Dispersión de Horas Críticas</b> la usa para elegir las HC y solo la muestra
          como informativo; aquí se captura o corrige.
        </p>
        {q.data?.actualizado_en && (
          <p className="text-xs text-slate-400 mt-1">
            Última edición: {fechaLarga(q.data.actualizado_en)} · {q.data.actualizado_por}
          </p>
        )}
      </div>

      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {(q.error as Error)?.message}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}
      {ok && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 text-sm inline-flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Periodo guardado. El reporte lo toma en la siguiente búsqueda.
        </div>
      )}
      {q.data && !q.data.tabla_lista && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          La tabla portal.configuracion aún no existe en la base de datos (correr
          backend/sql/portal_configuracion.sql como postgres). Se muestra el respaldo en código,
          pero no se podrá guardar hasta crearla.
        </div>
      )}

      {q.data && (
        <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-slate-800">Ventanas por año</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Los años marcados <i>respaldo en código</i> no se han capturado aquí; edítalos para
                confirmarlos.
              </div>
            </div>
            <button
              type="button"
              onClick={nuevoAnio}
              disabled={!!edit}
              className="inline-flex items-center gap-1.5 text-sm border rounded px-3 py-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> Agregar año
            </button>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">Año</th>
                <th className="text-left px-4 py-2">Sistema</th>
                <th className="text-left px-4 py-2">Desde</th>
                <th className="text-left px-4 py-2">Hasta (inclusive)</th>
                <th className="text-left px-4 py-2">Origen</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {edit && !filas.some((p) => p.anio === edit.anio && p.sistema === edit.sistema) && (
                <FilaEdicion
                  edit={edit}
                  setEdit={setEdit}
                  valido={editValido}
                  guardando={guardar.isPending}
                  onGuardar={() => guardar.mutate(edit)}
                  nuevo
                />
              )}
              {filas.map((p) =>
                edit?.anio === p.anio && edit?.sistema === p.sistema ? (
                  <FilaEdicion
                    key={`${p.anio}-${p.sistema}`}
                    edit={edit}
                    setEdit={setEdit}
                    valido={editValido}
                    guardando={guardar.isPending}
                    onGuardar={() => guardar.mutate(edit)}
                  />
                ) : (
                  <tr key={`${p.anio}-${p.sistema}`} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium text-slate-800">{p.anio}</td>
                    <td className="px-4 py-2 font-medium text-slate-600">{p.sistema}</td>
                    <td className="px-4 py-2 tabular-nums">{fmtFecha(p.desde)}</td>
                    <td className="px-4 py-2 tabular-nums">{fmtFecha(p.hasta)}</td>
                    <td className="px-4 py-2 text-xs">
                      {p.fuente === "configuracion" ? (
                        <span className="text-emerald-700">capturado</span>
                      ) : (
                        <span className="text-amber-700 italic">respaldo en código</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => editar(p)}
                        disabled={!!edit}
                        className="text-xs text-blue-700 hover:underline disabled:opacity-50 mr-3"
                      >
                        Editar
                      </button>
                      {p.fuente === "configuracion" && (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`¿Quitar el periodo de ${p.sistema} en ${p.anio}?`))
                              borrar.mutate({ anio: p.anio, sistema: p.sistema });
                          }}
                          disabled={!!edit || borrar.isPending}
                          title="Quitar el periodo capturado"
                          className="text-slate-400 hover:text-red-600 disabled:opacity-50 align-middle"
                        >
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                ),
              )}
              {filas.length === 0 && !edit && (
                <tr>
                  <td colSpan={6} className="px-4 py-4 text-sm text-slate-500">
                    Sin periodos capturados. Agrega el año.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function FilaEdicion({
  edit,
  setEdit,
  valido,
  guardando,
  onGuardar,
  nuevo = false,
}: {
  edit: Edit;
  setEdit: (e: Edit | null) => void;
  valido: boolean;
  guardando: boolean;
  onGuardar: () => void;
  nuevo?: boolean;
}) {
  const cambiarAnio = (a: number) => {
    // Al cambiar el año, las fechas conservan mes y día.
    const md = (iso: string) => (iso ? iso.slice(4) : "");
    setEdit({ ...edit, anio: a, desde: a ? `${a}${md(edit.desde)}` : edit.desde, hasta: a ? `${a}${md(edit.hasta)}` : edit.hasta });
  };
  return (
    <tr className="bg-blue-50/40">
      <td className="px-4 py-2">
        {nuevo ? (
          <input
            type="number"
            className="border rounded px-2 py-1 text-sm w-24"
            value={edit.anio}
            min={2017}
            max={2100}
            onChange={(e) => cambiarAnio(Number(e.target.value))}
          />
        ) : (
          <span className="font-medium text-slate-800">{edit.anio}</span>
        )}
      </td>
      <td className="px-4 py-2">
        {nuevo ? (
          <select
            className="border rounded px-2 py-1 text-sm"
            value={edit.sistema}
            onChange={(e) => setEdit({ ...edit, sistema: e.target.value })}
          >
            {SISTEMAS_HC.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        ) : (
          <span className="font-medium text-slate-600">{edit.sistema}</span>
        )}
      </td>
      <td className="px-4 py-2">
        <input
          type="date"
          className="border rounded px-2 py-1 text-sm"
          value={edit.desde}
          min={`${edit.anio}-01-01`}
          max={`${edit.anio}-12-31`}
          onChange={(e) => setEdit({ ...edit, desde: e.target.value })}
        />
      </td>
      <td className="px-4 py-2">
        <input
          type="date"
          className="border rounded px-2 py-1 text-sm"
          value={edit.hasta}
          min={`${edit.anio}-01-01`}
          max={`${edit.anio}-12-31`}
          onChange={(e) => setEdit({ ...edit, hasta: e.target.value })}
        />
      </td>
      <td className="px-4 py-2 text-xs text-slate-500">{valido ? "" : "dentro del año y desde ≤ hasta"}</td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        <button
          type="button"
          onClick={onGuardar}
          disabled={!valido || guardando}
          className="inline-flex items-center gap-1.5 bg-blue-600 text-white rounded px-3 py-1 text-xs hover:bg-blue-700 disabled:opacity-50 mr-2"
        >
          <Save className="w-3.5 h-3.5" /> {guardando ? "Guardando…" : "Guardar"}
        </button>
        <button
          type="button"
          onClick={() => setEdit(null)}
          disabled={guardando}
          className="text-xs text-slate-600 hover:underline"
        >
          Cancelar
        </button>
      </td>
    </tr>
  );
}
